import type { OpsRequest } from '../types/ops'

export interface RequestViewerContext {
  uid: string
  isOwner?: boolean
  role?: string
  sectorRoles?: string[]
  now?: number
}

export interface RequestActionsAllowed {
  canAcknowledge: boolean
  canStart: boolean
  canAssign: boolean
  canMarkDone: boolean
  canVerify: boolean
  canRelease: boolean
  mustSelfVerifyWithReason: boolean
  canReopen: boolean
  canCancel: boolean
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function requestActionsFor(
  request: OpsRequest,
  viewer: RequestViewerContext,
): RequestActionsAllowed {
  const isManager = Boolean(
    viewer.isOwner || viewer.role === 'ADMIN' || viewer.role === 'EDITOR',
  )
  const isAssignee = request.assigneeUid === viewer.uid
  const isEscalated =
    Array.isArray(request.escalations) &&
    request.escalations.some((e) => e.uids.includes(viewer.uid))
  const isRaiser = request.raisedByUid === viewer.uid

  const canAckOrStartActor = isAssignee || isEscalated || isManager

  // 1. Acknowledge: OPEN state only, assignee / escalated / manager
  const canAcknowledge = request.state === 'OPEN' && canAckOrStartActor

  // 2. Start: OPEN, ACKNOWLEDGED, or REOPENED state, assignee / escalated / manager
  const canStart =
    (request.state === 'OPEN' ||
      request.state === 'ACKNOWLEDGED' ||
      request.state === 'REOPENED') &&
    canAckOrStartActor

  // 3. Assign: Managers only, while request is not completed or cancelled
  const isTerminal =
    request.state === 'DONE' ||
    request.state === 'VERIFIED' ||
    request.state === 'CANCELLED'
  const canAssign = isManager && !isTerminal

  const hasDutyRole = Boolean(
    request.kindSnapshot?.dutyRoleKey &&
      viewer.sectorRoles &&
      viewer.sectorRoles.includes(request.kindSnapshot.dutyRoleKey),
  )

  // 4. Mark done: OPEN / ACKNOWLEDGED / IN_PROGRESS / REOPENED, assignee or manager
  const canMarkDone =
    (request.state === 'OPEN' ||
      request.state === 'ACKNOWLEDGED' ||
      request.state === 'IN_PROGRESS' ||
      request.state === 'REOPENED') &&
    (isAssignee || isManager || hasDutyRole)

  // 5. Verify: DONE state, verifyRequired is true, verifier role
  const isVerifier = Boolean(
    viewer.isOwner ||
      viewer.role === 'ADMIN' ||
      (viewer.sectorRoles && viewer.sectorRoles.includes('REQUEST_VERIFIER')) ||
      (request.kindSnapshot?.verifierRoleKey &&
        viewer.sectorRoles &&
        viewer.sectorRoles.includes(request.kindSnapshot.verifierRoleKey)),
  )

  let canVerify = false
  let mustSelfVerifyWithReason = false

  if (request.state === 'DONE' && request.kindSnapshot?.verifyRequired) {
    if (request.doneByUid === viewer.uid) {
      if (viewer.isOwner && !request.kindSnapshot?.strictDifferentPerson) {
        canVerify = true
        mustSelfVerifyWithReason = true
      }
    } else if (
      request.raisedByUid === viewer.uid &&
      request.kindSnapshot?.verifierNotRaiser
    ) {
      canVerify = false
      mustSelfVerifyWithReason = false
    } else if (isVerifier) {
      canVerify = true
      mustSelfVerifyWithReason = false
    }
  }

  const isBreakdownOrAssetRelease =
    request.kind === 'BREAKDOWN' ||
    request.kindSnapshot?.key === 'BREAKDOWN' ||
    Boolean(request.kindSnapshot?.isolateAssetOnRaise)

  const isReleaser = Boolean(
    viewer.isOwner ||
      viewer.role === 'ADMIN' ||
      (viewer.sectorRoles && viewer.sectorRoles.includes('ASSET_RELEASER')),
  )

  const isQualityHold =
    request.kind === 'QUALITY_HOLD' || request.kindSnapshot?.key === 'QUALITY_HOLD'

  const isQualityReleaser = Boolean(
    viewer.isOwner ||
      viewer.role === 'ADMIN' ||
      (viewer.sectorRoles && viewer.sectorRoles.includes('QUALITY_RELEASER')),
  )

  const canRelease = Boolean(
    canVerify &&
      ((isBreakdownOrAssetRelease && isReleaser) || (isQualityHold && isQualityReleaser)),
  )

  // 6. Reopen: DONE or VERIFIED, within 7 days, raiser or verifier
  const now = viewer.now ?? Date.now()
  const completionTime = request.verifiedAt || request.doneAt || 0
  const within7Days = now - completionTime <= SEVEN_DAYS_MS

  const canReopen =
    (request.state === 'DONE' || request.state === 'VERIFIED') &&
    within7Days &&
    (isRaiser || isVerifier)

  // 7. Cancel: not terminal, raiser or manager
  const canCancel = !isTerminal && (isRaiser || isManager)

  return {
    canAcknowledge,
    canStart,
    canAssign,
    canMarkDone,
    canVerify,
    canRelease,
    mustSelfVerifyWithReason,
    canReopen,
    canCancel,
  }
}
